<template>
    <div
        v-if="showBreadCrumbsFromRoute"
        class="row"
        :class="{
            'q-pl-sm bg-white': isXSmall,
            'q-pl-lg q-mt-md': isLargerThanMedium,
            'q-pl-md q-mt-sm': !isLargerThanMedium && !isXSmall,
        }"
    >
        <BreadCrumbsFromRoute />
    </div>
    <div
        v-if="showTitle || slots.tabs || slots.rightMenu || leftIcon"
        class="row no-wrap page-header-container"
        :class="{
            'full-width': !isXSmall,
            'q-pl-sm bg-white': isXSmall,
            'q-pl-lg': isLargerThanMedium,
            'q-pl-lg q-pb-md': isLargerThanMedium && !slots.tabs,
            'q-mt-md': !showBreadCrumbsFromRoute && isLargerThanMedium,
            'q-pt-sm': !showBreadCrumbsFromRoute,
            'q-py-xs': !showBreadCrumbsFromRoute && isXSmall,
            'q-pb-sm': !slots.tabs,
            'q-pl-md': !isLargerThanMedium && !isXSmall,
            'q-pr-sm': $q.screen.lt.xl,
        }"
        :style="isXSmall ? 'padding-left: 8px;' : 'min-height: 40px'"
    >
        <div
            v-if="(leftIcon && !onlyShowLeftIconMobile) || (onlyShowLeftIconMobile && $q.screen.lt.sm)"
            class="q-mr-sm"
            :class="{ 'q-mt-sm': slots.headerDescription, 'q-mt-xs': !slots.headerDescription }"
        >
            <div class="left-icon-container">
                <q-icon
                    flat
                    round
                    class="cursor-pointer"
                    size="xs"
                    :name="leftIcon"
                    @click="emit('onLeftIconClick')"
                    data-qs="page-header-left-icon"
                >
                    <q-tooltip v-if="leftIconTooltipText">{{ leftIconTooltipText }}</q-tooltip>
                </q-icon>
            </div>
        </div>
        <div class="flex column flex-basis-auto q-mr-sm no-wrap overflow-hidden">
            <div v-if="showTitle" class="flex" :class="{ 'no-wrap': $q.screen.gt.xs, column: $q.screen.lt.sm }">
                <div class="flex qit-text-ellipsis">
                    <h1 v-if="loading" class="heading-style skeleton-square q-my-none title-loading" />
                    <h1 v-else class="heading-style qit-text-ellipsis q-my-none" data-qs="page-header-title">
                        <template v-if="slots.title">
                            <slot name="title" />
                        </template>
                        <template v-else>{{ title }}</template>
                    </h1>
                </div>
                <div
                    v-if="slots.titleAfter && !loading"
                    class="flex content-start"
                    :class="{ 'q-ml-md q-mt-sm': $q.screen.gt.xs, 'q-mb-sm': $q.screen.lt.sm }"
                >
                    <slot name="titleAfter" />
                </div>
            </div>
            <div class="row header-description ellipsis-2-lines" v-if="!loading && slots.headerDescription">
                <slot name="headerDescription" />
            </div>
        </div>
        <div v-if="slots.headerAfter && !loading" class="flex content-start q-mt-sm">
            <slot name="headerAfter" />
        </div>
        <div
            v-if="slots.rightMenu && (alwaysShowRightMenu || !isXSmall) && !loading"
            class="right-menu row flex-grow justify-end col-gap-m items-center"
        >
            <slot name="rightMenu" />
        </div>
    </div>
    <template v-if="slots.tabs && !isXSmall">
        <slot name="tabs" />
    </template>
    <!--Mobile Only-->
    <div v-if="slots.tabs && isXSmall" class="row bg-white">
        <slot name="tabs" />
    </div>
    <!--Mobile Only End-->
</template>

<script setup lang="ts">
import BreadCrumbsFromRoute from "@/shared/components/breadcrumbs/breadcrumbs-from-route.vue";
import { useScreenSize } from "@/shared/screen/composables/screen-size";
import { computed, useSlots } from "vue";

const props = defineProps<{
    title?: string;
    loading?: boolean;
    leftIcon?: string;
    leftIconTooltipText?: string;
    onlyShowLeftIconMobile?: boolean;
    showBreadCrumbsFromRoute?: boolean;
    showTitleOnlyMobile?: boolean;
    alwaysShowRightMenu?: boolean;
}>();

const emit = defineEmits<{
    (e: "onLeftIconClick"): void;
}>();

const slots = useSlots();
const { isXSmall, isLargerThanMedium } = useScreenSize();

const showTitle = computed(() => {
    return (
        (props.showTitleOnlyMobile && isXSmall.value) || (!props.showTitleOnlyMobile && (props.title || props.loading))
    );
});
</script>

<style lang="scss" scoped>
.page-header-container {
    background-color: $background-color-light;

    .header-after {
        margin-top: 12px;
    }

    .header-description {
        color: $text-color-medium;
        font-weight: $semi-bold;
        font-size: 12px;
    }

    .left-icon-container {
        max-height: 34px;
    }

    .header-after-small {
        font-size: 12px;
        font-weight: $semi-bold;
    }

    .header-after-with-left-icon {
        margin-left: 35px;
    }
}

.header-after {
    font-weight: 600;
    font-size: 0.85rem;
    color: rgb(140, 140, 140);
}

.heading-style {
    font-size: 24px;

    .screen--xs & {
        max-width: calc(100vw - 38px); // 100 viewport width - leftIcon width
    }
}
.title-loading {
    height: 26px;
    width: 275px;
}
</style>
